import { Alert, Button, Checkbox, Col, Form, Row, Space, Tag, Typography, theme } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { originLabel } from "./CalendarView.default";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { EventParticipantRole, InviteResponse } from "../repository/data.default";
import {
  acceptCalendarInvitation,
  addCalendarDepartmentInvitations,
  addCalendarEvent,
  declineCalendarInvitation,
  deleteCalendarEvent,
  updateCalendarEvent,
} from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { Icons } from "../assets/icons";
import Icon from "@ant-design/icons";
import DynamicForm from "../controls/DynamicForm";
import { MyCalendarEventForm } from "./MyCalendarEventDialog.default";
import {
  addEventInvitations,
  allDepartmentMembers,
  buildCalendarEventRequests,
  coreEventFields,
  ParticipantItem,
} from "./calendar/calendarEventForm";
import DepartmentsMultiSelect from "./calendar/DepartmentsMultiSelect";
import EventParticipantsPanel from "./calendar/EventParticipantsPanel";
import ParticipantTargetSelect from "./calendar/ParticipantTargetSelect";
import InModal from "../controls/InModal";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { InputObject } from "../controls/default";

/** A titled, bordered panel — keeps Departments and Attendees visibly separate (theme-aware). */
function Section(props: { title: string; children: React.ReactNode }) {
  const { token } = theme.useToken();
  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-solid p-3"
      style={{ borderColor: token.colorBorderSecondary, backgroundColor: token.colorFillQuaternary }}
    >
      <Typography.Text strong>{props.title}</Typography.Text>
      {props.children}
    </section>
  );
}

/** Vertical hairline between the dialog's columns, fading out at both ends (theme-aware). */
function ColumnDivider() {
  const { token } = theme.useToken();
  return (
    <div
      aria-hidden
      className="w-px shrink-0 self-stretch"
      style={{
        background: `linear-gradient(to bottom, transparent, ${token.colorBorder} 12%, ${token.colorBorder} 88%, transparent)`,
      }}
    />
  );
}

export interface MyCalendarEventDialogProps {
  readonly open?: boolean;
  readonly title?: string;
  readonly mode: "preview" | "add" | "update" | "delete";
  readonly initialValues?: MyCalendarEventForm | null;
  readonly onMode?: (mode: "update" | "delete") => void;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function MyCalendarEventDialog(props: MyCalendarEventDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<MyCalendarEventForm>([], form);
  const [staged, setStaged] = useState<ParticipantItem[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<InputObject[]>([]);
  // "Add all": every member of the selected departments becomes an attendee.
  const [addAll, setAddAll] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // In MyCalendar the Company is always the signed-in admin's own company — read it from the JWT
  // (claims `usrCmpUid` / `usrCmpName` stamped at login) and lock the field so the admin can only
  // create events for their own company. The /api/Auth/Profile DTO doesn't expose these at top level.
  const jwtPayload = useSelector(authorizeSelectors.payload);
  // The signed-in creator hosts a new event, so they're never offered as one of its attendees.
  const signedInUserUid = useSelector(authorizeSelectors.user)?.userId;
  const lockedCompany: InputObject | undefined = useMemo(() => {
    const uid = (jwtPayload as any)?.usrCmpUid as string | undefined;
    const name = (jwtPayload as any)?.usrCmpName as string | undefined;
    return uid ? { display: name ?? "", value: uid } : undefined;
  }, [jwtPayload]);

  const requests = buildCalendarEventRequests(form);
  // Company is locked to the admin's own, so it renders read-only in the middle column (beside
  // Departments) rather than as a form field among the event details.
  const company = lockedCompany ?? props.initialValues?.company;
  const detailFields = coreEventFields(requests, props.mode === "preview", true).filter(
    (control) => control.name !== "company",
  );
  const isPreview = props.mode === "preview";
  // Edit/Delete carry the fetched event's `uid`; Preview comes from the personal feed, where the row's own
  // uid is the invitation's — the event is `eventUid`. Either way this is the persisted event (none while
  // adding), so Preview loads the event's real participants instead of the empty "new event" list.
  const eventUid = props.initialValues?.uid ?? props.initialValues?.eventUid;

  // A normal user's calendar: you manage an event only if you host/created it; an attendee can only
  // respond. System-managed (origin) events are read-only here regardless — managed from their source.
  const isOrigin = !!props.initialValues?.originType;
  const isHost = props.initialValues?.role === EventParticipantRole.Host;
  const canManage = !isOrigin && isHost;
  const canRespond = props.initialValues?.role === EventParticipantRole.Attendee;

  // Pickers are scoped to the event's company; switching it drops staged picks from the previous company
  // (only on an actual change, not the initial set / population).
  const prevCompanyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const current = company?.value;
    if (prevCompanyRef.current !== undefined && current !== prevCompanyRef.current) {
      setStaged([]);
    }
    prevCompanyRef.current = current;
  }, [company?.value]);

  // Attendees are picked from the selected departments — removing a department drops the staged
  // (not yet invited) attendees that came from it.
  useEffect(() => {
    const uids = new Set(selectedDepartments.map((d) => d.value));
    setStaged((prev) => {
      const next = prev.filter(
        (item) => !item.dataItem?.__departmentUid || uids.has(item.dataItem.__departmentUid),
      );
      return next.length === prev.length ? prev : next;
    });
  }, [selectedDepartments]);

  // New event + "Add all" ticked: stage every member of the selected departments, and keep doing so as
  // departments are added (removed ones are already pruned above). Hand-picked attendees are kept as-is.
  useEffect(() => {
    const uids = selectedDepartments.map((d) => d.value);
    if (eventUid || !addAll || !company?.value || !uids.length) return;
    let active = true;
    allDepartmentMembers(company.value, uids)
      .then((members) => {
        if (!active) return;
        setStaged((prev) => {
          const known = new Set(prev.map((item) => item.value));
          const added = members.filter(
            (m) => !known.has(m.value) && m.value !== signedInUserUid,
          );
          return added.length ? [...prev, ...added] : prev;
        });
      })
      .catch((err) => active && setErrorMessage(getProblemMessage(err)));
    return () => {
      active = false;
    };
  }, [addAll, selectedDepartments, company?.value, eventUid, signedInUserUid]);

  useEffect(() => {
    if (!props.open) {
      reset();
      prevCompanyRef.current = undefined;
      return;
    }
    // Existing events load their participants in the panel; nothing to preload here.
    // Company is always the admin's own in MyCalendar — stamp it over whatever the preview carries.
    const initial = {
      ...(props.initialValues ?? ({} as any)),
      company: lockedCompany ?? props.initialValues?.company,
    } as MyCalendarEventForm;
    form.setFieldsValue(initial);
    setSelectedDepartments((props.initialValues?.departments as InputObject[] | undefined) ?? []);
  }, [props.open, props.initialValues]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  // Events

  // Add a picked attendee: immediately on an existing event (panel refetches), or staged while creating.
  async function addPicked(picked?: ParticipantItem): Promise<void> {
    if (!picked?.value) return;

    if (eventUid) {
      setLoading(true);
      setErrorMessage("");
      try {
        await addEventInvitations(eventUid, [picked]);
        setRefreshKey((k) => k + 1);
      } catch (err) {
        setErrorMessage(getProblemMessage(err));
      } finally {
        setLoading(false);
      }
    } else {
      const keyOf = (item: ParticipantItem) => `${item.dataItem?.__type}|${item.value}`;
      setStaged((prev) =>
        prev.some((item) => keyOf(item) === keyOf(picked)) ? prev : [...prev, picked],
      );
    }
  }

  function onAddAllChange(checked: boolean): void {
    if (!eventUid) {
      // Staged: ticking loads everyone (effect above). "Add All" means everyone, so unticking clears
      // the whole list — hand-picked attendees included — like the Departments "Add All".
      setAddAll(checked);
      if (!checked) setStaged([]);
      return;
    }
    // Existing event: invites are immediate — one whole-department invite per selected department.
    // The box then stays ticked (and locked): mass-uninviting is not offered here.
    if (!checked) return;
    setLoading(true);
    setErrorMessage("");
    Promise.all(
      selectedDepartments.map((d) =>
        addCalendarDepartmentInvitations({ departmentUid: d.value, eventUid }),
      ),
    )
      .then(() => {
        setAddAll(true);
        setRefreshKey((k) => k + 1);
      })
      .catch((err) => setErrorMessage(getProblemMessage(err)))
      .finally(() => setLoading(false));
  }

  // Removing one attendee (from the table or by unticking it in the dropdown) means "all" no longer
  // holds — turn Add All off without touching the rest.
  function onUnpickStaged(item: ParticipantItem): void {
    setStaged((prev) => prev.filter((staged) => staged.value !== item.value));
    setAddAll(false);
  }

  function onRemoveStaged(index: number): void {
    setAddAll(false);
    setStaged((prev) => prev.filter((_, i) => i !== index));
  }

  function onSubmit(): void {
    const fail = (err?: any) => {
      setErrorMessage(getProblemMessage(err));
      setLoading(false);
    };

    if (props.mode === "add") {
      const { title, description, startDateTime, endDateTime, type } = values;
      setLoading(true);
      setErrorMessage("");
      // The creator is the host; everyone else picked here is an attendee.
      addCalendarEvent({
        title,
        description,
        startDateTime: startDateTime.toDate(),
        endDateTime: endDateTime.toDate(),
        companyUid: lockedCompany?.value,
        departmentUids: selectedDepartments.map((d) => d.value),
        typeUid: type.value!,
      })
        .then((dataItem) =>
          addEventInvitations(dataItem.uid, staged)
            .then(() => {
              setLoading(false);
              props.onFinish?.();
            })
            .catch(fail),
        )
        .catch(fail);
    } else if (props.mode === "update") {
      const { title, description, startDateTime, endDateTime, type } = values;
      setLoading(true);
      setErrorMessage("");
      // Participants are managed immediately; Save only commits the event details.
      updateCalendarEvent({
        uid: props.initialValues?.uid!,
        rowVersion: props.initialValues?.rowVersion!,
        title,
        description,
        startDateTime: startDateTime.toDate(),
        endDateTime: endDateTime.toDate(),
        companyUid: lockedCompany?.value,
        departmentUids: selectedDepartments.map((d) => d.value),
        typeUid: type.value!,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(fail);
    } else if (props.mode === "delete") {
      setLoading(true);
      setErrorMessage("");
      deleteCalendarEvent(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(fail);
    }
  }

  function onMode(mode: "update" | "delete"): void {
    props.onMode?.(mode);
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // The invitee accepts/declines their own invitation for this event.
  function onRespond(accept: boolean): void {
    const respondEventUid = props.initialValues?.eventUid;
    if (!respondEventUid) return;
    setLoading(true);
    setErrorMessage("");
    (accept ? acceptCalendarInvitation : declineCalendarInvitation)(respondEventUid)
      .then(() => {
        setLoading(false);
        props.onFinish?.();
      })
      .catch((err) => {
        setErrorMessage(getProblemMessage(err));
        setLoading(false);
      });
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage("");
    setStaged([]);
    setSelectedDepartments([]);
    setAddAll(false);
    setRefreshKey(0);
    form.resetFields();
  }

  function createTitle(): string {
    switch (props.mode) {
      case "add":
        return "Add Event";
      case "update":
        return "Edit Event";
      case "delete":
        return "Remove Event";
      case "preview":
        return "Preview Event";
    }
  }

  function createMode(): string {
    switch (props.mode) {
      case "add":
        return "Add";
      case "update":
        return "Save";
      case "preview":
      case "delete":
        return "Delete";
    }
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "large"}
      title={
        <Row className="flex flex-row items-center overflow-y-auto">
          <Typography.Text className="flex-auto">{createTitle()}</Typography.Text>
          {isPreview && originLabel(props.initialValues?.originType) && (
            <Tag color="purple" className="mr-2 self-center">
              {originLabel(props.initialValues?.originType)}
            </Tag>
          )}
          {isPreview && (
            <Space className="-mt-2 mr-10 self-end">
              {canRespond && (
                <>
                  <Typography.Text type="secondary" className="self-center">
                    {props.initialValues?.response === InviteResponse.Accepted
                      ? "Accepted"
                      : props.initialValues?.response === InviteResponse.Declined
                        ? "Declined"
                        : "Pending"}
                  </Typography.Text>
                  <Button
                    size="small"
                    type="primary"
                    disabled={loading || props.initialValues?.response === InviteResponse.Accepted}
                    onClick={() => onRespond(true)}
                  >
                    Accept
                  </Button>
                  <Button
                    size="small"
                    disabled={loading || props.initialValues?.response === InviteResponse.Declined}
                    onClick={() => onRespond(false)}
                  >
                    Decline
                  </Button>
                </>
              )}
              {canManage && (
                <>
                  <Button
                    type={"text"}
                    icon={<Icon className="-ml-0.5 text-2xl" component={Icons.EditIcon} />}
                    onClick={() => onMode("update")}
                  />
                  <Button
                    type={"text"}
                    icon={<Icon className="-ml-0.5 text-2xl" component={Icons.DeleteIcon} />}
                    onClick={() => onMode("delete")}
                  />
                </>
              )}
            </Space>
          )}
        </Row>
      }
      onCancel={onCancel}
      actions={
        !isPreview && (
          <Space>
            <Button disabled={loading} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type={"primary"}
              danger={props.mode === "delete"}
              loading={loading}
              disabled={!valid && props.mode !== "delete"}
              onClick={onSubmit}
            >
              {createMode()}
            </Button>
          </Space>
        )
      }
    >
      {props.mode !== "delete" && (
        // Two fixed-height columns that scroll independently — event details | who it's for (company,
        // departments, attendees) — so a growing department or attendee list never stretches the dialog.
        <Row className="mb-2 flex h-[680px] flex-row flex-nowrap gap-5 overflow-hidden">
          <Col className="flex min-w-0 flex-[5] flex-col overflow-y-auto overflow-x-hidden">
            <DynamicForm
              className="w-full"
              disabled={loading}
              form={form}
              preview={isPreview}
              schema={{ groups: [{ controls: detailFields }] }}
            />
          </Col>
          <ColumnDivider />
          <Col className="flex min-w-0 flex-[6] flex-col gap-3 overflow-y-auto overflow-x-hidden p-1">
            <div className="flex flex-col gap-2">
              <Typography.Text className="text-sm" type="secondary">
                Company
              </Typography.Text>
              {/* Locked to the admin's own company — plain text, never an input. */}
              <Typography.Text className="pl-2">{company?.display}</Typography.Text>
            </div>
            {!isPreview && (
              // PD-01 §9 — three attendee-targeting modes, all expressible with
              // the fields below. Spelled out so admins don't have to infer them.
              <Alert
                type="info"
                showIcon
                message="Choose who gets this event"
                description={
                  <ul className="m-0 list-disc pl-5">
                    <li>Leave Departments empty → the whole company is invited.</li>
                    <li>Pick Departments → everyone in them (toggle "Add All") or specific people from them.</li>
                    <li>Hand-pick individuals in Attendees for a focused group.</li>
                  </ul>
                }
              />
            )}
            {/* Departments and Attendees are separate targeting sections, each in its own panel. */}
            <Section title="Departments">
              <DepartmentsMultiSelect
                companyUid={company?.value}
                value={selectedDepartments}
                onChange={setSelectedDepartments}
                readOnly={isPreview}
                disabled={loading}
                loadDepartments={requests.scopedDepartmentsRequest}
                allowAddAll
              />
            </Section>
            <Section title="Attendees">
              {!isPreview && (
                <ParticipantTargetSelect
                  className="w-full"
                  companyUid={company?.value}
                  departmentUids={selectedDepartments.map((d) => d.value)}
                  placeholder="Search and add attendees"
                  disabled={loading}
                  onPick={addPicked}
                  // New event: the dropdown doubles as the attendee checklist — picked people show
                  // checked and clicking one again removes them.
                  selectedValues={eventUid ? undefined : staged.map((item) => item.value)}
                  onUnpick={eventUid ? undefined : onUnpickStaged}
                  excludeValues={!eventUid && signedInUserUid ? [signedInUserUid] : undefined}
                  dropdownHeader={
                    <Checkbox
                      checked={addAll}
                      disabled={loading || !selectedDepartments.length || (!!eventUid && addAll)}
                      onChange={(e) => onAddAllChange(e.target.checked)}
                    >
                      Add All
                    </Checkbox>
                  }
                />
              )}
              <EventParticipantsPanel
                eventUid={eventUid}
                staged={staged}
                editable={!isPreview}
                refreshKey={refreshKey}
                onRemoveStaged={onRemoveStaged}
              />
            </Section>
          </Col>
        </Row>
      )}
      {isPreview && isOrigin && (
        <Alert
          className="mt-1"
          type="info"
          showIcon
          message={`This ${originLabel(props.initialValues?.originType)} is managed from its source flow. ${
            canRespond ? "You can still accept or decline your invitation here." : ""
          }`}
        />
      )}
      {props.mode === "delete" && (
        <Typography.Paragraph>
          <Typography.Text>
            Continue deleting the{" "}
            <Typography.Text className="font-bold">{props.title}</Typography.Text> event?
          </Typography.Text>
        </Typography.Paragraph>
      )}
      {errorMessage && (
        <Alert className="mb-2" message={errorMessage} type="error" />
      )}
    </InModal>
  );
}

export default MyCalendarEventDialog;
